import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  BottomNavigation,
  BottomNavigationAction,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab,
  Drawer,
  AppBar,
  Toolbar,
  Badge,
  Grid
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChatIcon from '@mui/icons-material/Chat';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CancelIcon from '@mui/icons-material/Cancel';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { useAuth } from '../contexts/AuthContext';
import { db, storage } from '../firebase/config';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc,
  serverTimestamp,
  addDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { COLLECTIONS } from '../firebase/collections';
import TechnicalServiceChat from '../components/Chat/TechnicalServiceChat';

const MobileServiceApp = () => {
  const [value, setValue] = useState(0);
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [openRequestDialog, setOpenRequestDialog] = useState(false);
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [updateData, setUpdateData] = useState({
    status: '',
    comment: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const { currentUser, userService } = useAuth();
  const fileInputRef = React.useRef();

  // Récupérer les demandes et les événements
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');

        // Si userService n'est pas défini, on vérifie d'abord dans la base de données
        let serviceId = userService?.id;
        if (!serviceId && currentUser) {
          const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, currentUser.uid));
          if (userDoc.exists() && userDoc.data().serviceId) {
            serviceId = userDoc.data().serviceId;
          }
        }

        if (!serviceId) {
          setError('Service technique non trouvé. Veuillez contacter votre administrateur.');
          setLoading(false);
          return;
        }

        // Récupérer les demandes assignées à ce service technique
        const requestsQuery = query(
          collection(db, COLLECTIONS.WORK_REQUESTS),
          where('assignedServiceId', '==', serviceId),
          orderBy('createdAt', 'desc')
        );

        const requestsSnapshot = await getDocs(requestsQuery);
        const requestsData = requestsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        }));

        // Récupérer les événements du calendrier
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const eventsQuery = query(
          collection(db, COLLECTIONS.CALENDAR_EVENTS),
          where('serviceId', '==', serviceId),
          where('start', '>=', today)
        );

        const eventsSnapshot = await getDocs(eventsQuery);
        const eventsData = eventsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            start: data.start.toDate(),
            end: data.end.toDate()
          };
        });

        // Trier les événements par date
        eventsData.sort((a, b) => a.start - b.start);

        setRequests(requestsData);
        setFilteredRequests(requestsData);
        setEvents(eventsData);
      } catch (err) {
        console.error('Erreur lors de la récupération des données:', err);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, userService]);

  // Filtrer les demandes
  useEffect(() => {
    let filtered = [...requests];
    
    if (searchTerm) {
      filtered = filtered.filter(req => 
        (req.title && req.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (req.description && req.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (req.requestingServiceName && req.requestingServiceName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (req.location && req.location.room && req.location.room.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(req => req.status === statusFilter);
    }
    
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(req => req.priority === priorityFilter);
    }
    
    setFilteredRequests(filtered);
  }, [searchTerm, statusFilter, priorityFilter, requests]);

  const handleOpenRequestDialog = (request) => {
    setSelectedRequest(request);
    setUpdateData({
      status: request.status || '',
      comment: ''
    });
    setImageFile(null);
    setImagePreview(null);
    setOpenRequestDialog(true);
  };

  const handleCloseRequestDialog = () => {
    setOpenRequestDialog(false);
    setSelectedRequest(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdateData({
      ...updateData,
      [name]: value
    });
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      
      // Créer un aperçu de l'image
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateRequest = async () => {
    if (!selectedRequest) return;
    
    try {
      const requestRef = doc(db, COLLECTIONS.WORK_REQUESTS, selectedRequest.id);
      
      // Données de mise à jour
      const updateFields = {
        status: updateData.status,
        updatedAt: serverTimestamp()
      };
      
      // Si un statut "completed" est sélectionné, ajouter la date de complétion
      if (updateData.status === 'completed') {
        updateFields.completedAt = serverTimestamp();
      }
      
      // Mettre à jour la demande
      await updateDoc(requestRef, updateFields);
      
      // Si un commentaire est ajouté, l'enregistrer
      if (updateData.comment.trim()) {
        let imageUrl = null;
        
        // Si une image est jointe, la télécharger d'abord
        if (imageFile) {
          const fileRef = ref(storage, `work_requests/${selectedRequest.id}/${Date.now()}_${imageFile.name}`);
          await uploadBytes(fileRef, imageFile);
          imageUrl = await getDownloadURL(fileRef);
        }
        
        // Ajouter le commentaire
        await addDoc(collection(db, COLLECTIONS.COMMENTS), {
          requestId: selectedRequest.id,
          userId: currentUser.uid,
          userName: userService?.name || currentUser.displayName || 'Utilisateur',
          userRole: 'technical',
          content: updateData.comment.trim(),
          imageUrl,
          createdAt: serverTimestamp()
        });
      }
      
      // Mettre à jour la liste des demandes
      const updatedRequests = requests.map(req => 
        req.id === selectedRequest.id 
          ? { ...req, status: updateData.status, updatedAt: new Date() }
          : req
      );
      
      setRequests(updatedRequests);
      handleCloseRequestDialog();
    } catch (err) {
      console.error('Erreur lors de la mise à jour de la demande:', err);
      setError('Erreur lors de la mise à jour');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <PendingIcon />;
      case 'in_progress': return <ScheduleIcon />;
      case 'completed': return <CheckCircleIcon />;
      case 'cancelled': return <CancelIcon />;
      default: return <AssignmentIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'in_progress': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (date) => {
    if (!date) return '';
    
    return date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 7 }}>
      {/* En-tête */}
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            EduWorks Mobile
          </Typography>
          <IconButton color="inherit" onClick={() => setOpenFilterDrawer(true)}>
            <FilterListIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {/* Contenu principal */}
      <Container sx={{ mt: 2, mb: 8 }}>
        {/* Onglet Demandes */}
        {value === 0 && (
          <>
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                placeholder="Rechercher une demande..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                }}
              />
            </Box>

            {filteredRequests.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  Aucune demande ne correspond aux critères de recherche
                </Typography>
              </Paper>
            ) : (
              <List sx={{ bgcolor: 'background.paper' }}>
                {filteredRequests.map((request) => (
                  <React.Fragment key={request.id}>
                    <Paper sx={{ mb: 2, overflow: 'hidden' }}>
                      <ListItem 
                        button 
                        onClick={() => handleOpenRequestDialog(request)}
                        sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
                      >
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle1" component="div">
                            {request.title || `Demande #${request.id.substring(0, 8)}`}
                          </Typography>
                          <Chip 
                            label={request.priority || 'Non défini'} 
                            size="small"
                            color={getPriorityColor(request.priority)}
                          />
                        </Box>
                        
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {request.requestingServiceName || 'Service non spécifié'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(request.createdAt)}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            {request.location ? 
                              `${request.location.building || ''} ${request.location.floor || ''} ${request.location.room || ''}`.trim() 
                              : 'Lieu non spécifié'}
                          </Typography>
                          <Chip 
                            icon={getStatusIcon(request.status)}
                            label={request.status || 'Non défini'} 
                            size="small"
                            color={getStatusColor(request.status)}
                          />
                        </Box>
                      </ListItem>
                    </Paper>
                  </React.Fragment>
                ))}
              </List>
            )}
          </>
        )}

        {/* Onglet Calendrier */}
        {value === 1 && (
          <>
            <Typography variant="h6" gutterBottom>
              Interventions planifiées
            </Typography>
            
            {events.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  Aucune intervention planifiée
                </Typography>
              </Paper>
            ) : (
              <List sx={{ bgcolor: 'background.paper' }}>
                {events.map((event) => (
                  <Paper key={event.id} sx={{ mb: 2, overflow: 'hidden' }}>
                    <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" component="div">
                          {event.title}
                        </Typography>
                        <Chip 
                          label={event.status} 
                          size="small"
                          color={getStatusColor(event.status)}
                        />
                      </Box>
                      
                      <Box sx={{ width: '100%', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          <CalendarTodayIcon sx={{ fontSize: 'small', mr: 0.5, verticalAlign: 'middle' }} />
                          {formatDate(event.start)} {formatTime(event.start)} - {formatTime(event.end)}
                        </Typography>
                      </Box>
                      
                      {event.location && (
                        <Box sx={{ width: '100%', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Lieu: {event.location}
                          </Typography>
                        </Box>
                      )}
                      
                      {event.description && (
                        <Box sx={{ width: '100%' }}>
                          <Typography variant="body2">
                            {event.description}
                          </Typography>
                        </Box>
                      )}
                    </ListItem>
                  </Paper>
                ))}
              </List>
            )}
          </>
        )}

        {/* Onglet Messages */}
        {value === 2 && (
          <>
            <Typography variant="h6" gutterBottom>
              Messages
            </Typography>
            
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Sélectionnez une demande pour voir les messages associés
              </Typography>
              
              <List sx={{ bgcolor: 'background.paper' }}>
                {requests.slice(0, 5).map((request) => (
                  <ListItem 
                    key={request.id}
                    button
                    onClick={() => setSelectedRequest(request)}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: getStatusColor(request.status) }}>
                        <ChatIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={request.title || `Demande #${request.id.substring(0, 8)}`}
                      secondary={`${request.requestingServiceName || 'Service non spécifié'} - ${formatDate(request.createdAt)}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
            
            {selectedRequest && (
              <TechnicalServiceChat requestId={selectedRequest.id} />
            )}
          </>
        )}

        {/* Onglet Profil */}
        {value === 3 && (
          <>
            <Typography variant="h6" gutterBottom>
              Profil
            </Typography>
            
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ width: 80, height: 80, mb: 2, bgcolor: 'primary.main' }}>
                  {userService?.name?.charAt(0) || currentUser?.displayName?.charAt(0) || 'U'}
                </Avatar>
                <Typography variant="h6">
                  {userService?.name || currentUser?.displayName || 'Utilisateur'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Service technique
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <List>
                <ListItem>
                  <ListItemText 
                    primary="Email" 
                    secondary={currentUser?.email || 'Non spécifié'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="École" 
                    secondary={userService?.schoolName || 'Non spécifié'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Demandes assignées" 
                    secondary={requests.length} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Demandes en cours" 
                    secondary={requests.filter(req => req.status === 'in_progress').length} 
                  />
                </ListItem>
              </List>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <Button variant="outlined" color="primary">
                  Paramètres
                </Button>
              </Box>
            </Paper>
          </>
        )}
      </Container>

      {/* Navigation du bas */}
      <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }} elevation={3}>
        <BottomNavigation
          showLabels
          value={value}
          onChange={(event, newValue) => {
            setValue(newValue);
          }}
        >
          <BottomNavigationAction label="Demandes" icon={<AssignmentIcon />} />
          <BottomNavigationAction label="Calendrier" icon={<CalendarTodayIcon />} />
          <BottomNavigationAction 
            label="Messages" 
            icon={
              <Badge badgeContent={unreadMessages} color="error">
                <ChatIcon />
              </Badge>
            } 
          />
          <BottomNavigationAction label="Profil" icon={<AccountCircleIcon />} />
        </BottomNavigation>
      </Paper>

      {/* Dialogue de détail de demande */}
      <Dialog 
        open={openRequestDialog} 
        onClose={handleCloseRequestDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {selectedRequest?.title || `Demande #${selectedRequest?.id.substring(0, 8)}`}
            </Typography>
            <IconButton onClick={handleCloseRequestDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRequest && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Détails de la demande
                </Typography>
                <Divider sx={{ my: 1 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Service demandeur
                    </Typography>
                    <Typography variant="body1">
                      {selectedRequest.requestingServiceName || 'Non spécifié'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Date de création
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(selectedRequest.createdAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Priorité
                    </Typography>
                    <Chip 
                      label={selectedRequest.priority || 'Non défini'} 
                      size="small"
                      color={getPriorityColor(selectedRequest.priority)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Statut actuel
                    </Typography>
                    <Chip 
                      label={selectedRequest.status || 'Non défini'} 
                      size="small"
                      color={getStatusColor(selectedRequest.status)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Localisation
                    </Typography>
                    <Typography variant="body1">
                      {selectedRequest.location ? 
                        `${selectedRequest.location.building || ''} ${selectedRequest.location.floor || ''} ${selectedRequest.location.room || ''}`.trim() 
                        : 'Non spécifié'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {selectedRequest.description || 'Aucune description'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Mettre à jour le statut
                </Typography>
                <Divider sx={{ my: 1 }} />
                
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>Statut</InputLabel>
                  <Select
                    name="status"
                    value={updateData.status}
                    onChange={handleInputChange}
                    label="Statut"
                  >
                    <MenuItem value="pending">En attente</MenuItem>
                    <MenuItem value="in_progress">En cours</MenuItem>
                    <MenuItem value="completed">Terminé</MenuItem>
                    <MenuItem value="cancelled">Annulé</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  name="comment"
                  label="Commentaire"
                  fullWidth
                  multiline
                  rows={3}
                  value={updateData.comment}
                  onChange={handleInputChange}
                  sx={{ mt: 2 }}
                />
                
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    onClick={() => fileInputRef.current.click()}
                    sx={{ mb: 2 }}
                  >
                    Ajouter une photo
                  </Button>
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                  
                  {imagePreview && (
                    <Box sx={{ mt: 2, position: 'relative' }}>
                      <img 
                        src={imagePreview} 
                        alt="Aperçu" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '200px',
                          borderRadius: '4px'
                        }} 
                      />
                      <IconButton
                        size="small"
                        sx={{ 
                          position: 'absolute', 
                          top: 0, 
                          right: 0,
                          bgcolor: 'rgba(0,0,0,0.5)',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'rgba(0,0,0,0.7)',
                          }
                        }}
                        onClick={() => {
                          setImageFile(null);
                          setImagePreview(null);
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRequestDialog}>Annuler</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleUpdateRequest}
          >
            Mettre à jour
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tiroir de filtres */}
      <Drawer
        anchor="right"
        open={openFilterDrawer}
        onClose={() => setOpenFilterDrawer(false)}
      >
        <Box sx={{ width: 250, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Filtres</Typography>
            <IconButton onClick={() => setOpenFilterDrawer(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Statut"
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="pending">En attente</MenuItem>
              <MenuItem value="in_progress">En cours</MenuItem>
              <MenuItem value="completed">Terminé</MenuItem>
              <MenuItem value="cancelled">Annulé</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Priorité</InputLabel>
            <Select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              label="Priorité"
            >
              <MenuItem value="all">Toutes</MenuItem>
              <MenuItem value="high">Haute</MenuItem>
              <MenuItem value="medium">Moyenne</MenuItem>
              <MenuItem value="low">Basse</MenuItem>
            </Select>
          </FormControl>
          
          <Button 
            variant="contained" 
            fullWidth
            onClick={() => {
              setStatusFilter('all');
              setPriorityFilter('all');
              setSearchTerm('');
            }}
          >
            Réinitialiser
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
};

export default MobileServiceApp;
